import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo, navigation, colors } from '../data';
import { Menu, X, Linkedin, Facebook, Instagram, Phone, Mail, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      } else {
        // If element is not rendered yet, try with a short timeout
        const timer = setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            const headerOffset = 100;
            const elementPosition = el.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

const Header = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = React.useState(null);
  const location = useLocation();

  useEffect(() => {
    if (!isOpen) {
      setActiveMobileSubmenu(null);
    }
  }, [isOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex flex-col transition-transform duration-300 hover:scale-105 origin-left">
            <span className={`text-2xl md:text-3xl font-serif font-semibold ${colors.contrast}`}>Pia Meyer</span>
            <span className="text-[11px] md:text-xs uppercase tracking-widest text-stone-500">Bedre økonomistyring med menneskelig ledelse</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex space-x-8 items-center">
            {navigation.map((item) => {
              const isActive = item.href === '/' 
                ? location.pathname === '/' 
                : location.pathname.startsWith(item.href);
              
              if (item.children) {
                return (
                  <div 
                    key={item.name} 
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={item.href}
                      className={`text-sm font-medium transition-all duration-300 flex items-center gap-1 py-2 ${
                        isActive ? 'text-[#204f3d]' : 'text-stone-600 hover:text-[#204f3d]'
                      }`}
                    >
                      {item.name}
                      <ChevronDown size={14} className={`transition-transform duration-300 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                    </Link>
                    
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 mt-0 w-64 bg-white border border-stone-100 shadow-xl rounded-2xl overflow-hidden py-2"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className="block px-6 py-3 text-sm text-stone-600 hover:bg-[#f7f4ed] hover:text-[#204f3d] transition-colors"
                            >
                              {child.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-all duration-300 relative py-2 group ${
                    isActive ? 'text-[#204f3d]' : 'text-stone-600 hover:text-[#204f3d]'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#204f3d] transform origin-left transition-transform duration-300 ${
                    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`} />
                </Link>
              );
            })}
            <a
              href="https://bookings.cloud.microsoft/bookwithme/user/12292401f48348aba20998fa72cc4ed6%40piameyer.dk?anonymous&ismsaljsauthenabled"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-6 py-2 rounded-full text-sm font-medium ${colors.contrastBg} text-white hover:opacity-90 transition-opacity ml-4`}
            >
              Book en samtale
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-stone-600" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-white border-b border-stone-100 max-h-[calc(100vh-80px)] overflow-y-auto"
          >
            <div className="px-4 pt-2 pb-6">
              <AnimatePresence mode="wait">
                {!activeMobileSubmenu ? (
                  <motion.div
                    key="main-menu"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-1"
                  >
                    {navigation.map((item) => {
                      const isActive = item.href === '/' 
                        ? location.pathname === '/' 
                        : location.pathname.startsWith(item.href);

                      if (item.children) {
                        return (
                          <button
                            key={item.name}
                            onClick={() => setActiveMobileSubmenu(item.name)}
                            className={`flex justify-between items-center w-full px-4 py-4 text-base font-medium rounded-lg transition-colors text-left ${
                              isActive 
                                ? 'text-[#204f3d] bg-[#f7f4ed]' 
                                : 'text-stone-600 hover:bg-stone-50'
                            }`}
                          >
                            <span>{item.name}</span>
                            <ChevronRight size={18} className="text-stone-400" />
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`block px-4 py-4 text-base font-medium rounded-lg transition-colors ${
                            isActive 
                              ? 'text-[#204f3d] bg-[#f7f4ed]' 
                              : 'text-stone-600 hover:bg-stone-50'
                          }`}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                    <div className="px-4 pt-4">
                      <Link
                        to="/kontakt"
                        onClick={() => setIsOpen(false)}
                        className={`block w-full text-center px-6 py-4 rounded-full text-sm font-medium ${colors.contrastBg} text-white shadow-lg`}
                      >
                        Book en samtale
                      </Link>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="sub-menu"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Back Button */}
                    <button
                      onClick={() => setActiveMobileSubmenu(null)}
                      className="flex items-center gap-2 px-4 py-3 text-stone-600 hover:text-[#204f3d] font-medium text-sm border-b border-stone-100 w-full text-left"
                    >
                      <ChevronLeft size={16} />
                      <span>Tilbage</span>
                    </button>

                    {/* Category Title */}
                    <div className="px-4">
                      <span className="text-xs uppercase tracking-widest text-stone-400 font-semibold">
                        {activeMobileSubmenu}
                      </span>
                    </div>

                    {/* Sub-points */}
                    <div className="space-y-1">
                      {(() => {
                        const activeItem = navigation.find(item => item.name === activeMobileSubmenu);
                        if (!activeItem) return null;
                        
                        return (
                          <>
                            <Link
                              to={activeItem.href}
                              onClick={() => setIsOpen(false)}
                              className="block px-4 py-3 text-base font-medium text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
                            >
                              Oversigt over {activeItem.name.toLowerCase()}
                            </Link>

                            {activeItem.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                onClick={() => setIsOpen(false)}
                                className="block px-4 py-3 text-base text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#204f3d] text-[#f7f4ed] pt-20 pb-10 border-t border-[#204f3d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex flex-col mb-6">
              <span className="text-2xl font-serif font-semibold text-white">Pia Meyer</span>
              <span className="text-xs uppercase tracking-widest text-stone-300">Meyer Management ApS</span>
            </Link>
            <p className="text-stone-200 text-sm leading-relaxed mb-6">
              Professionel rådgivning, sparring og ledelsesudvikling med mennesket i centrum.
            </p>
            <div className="flex space-x-4">
              <a href={companyInfo.socials.linkedin} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-white transition-colors">
                <Linkedin size={20} />
              </a>
              <a href={companyInfo.socials.facebook} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-white transition-colors">
                <Facebook size={20} />
              </a>
              <a href={companyInfo.socials.instagram} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-white transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li className="flex items-center space-x-3 text-stone-200 text-sm">
                <Phone size={16} className="text-stone-300" />
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{companyInfo.phone}</a>
              </li>
              <li className="flex items-center space-x-3 text-stone-200 text-sm">
                <Mail size={16} className="text-stone-300" />
                <a href={`mailto:${companyInfo.email}`} className="hover:text-white transition-colors">{companyInfo.email}</a>
              </li>
              <li className="text-stone-200 text-sm pt-2">
                CVR: {companyInfo.cvr}
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white mb-6">Meyer Management</h4>
            <ul className="space-y-4">
              <li><Link to="/om-mig" className="text-stone-200 hover:text-white text-sm transition-colors">Om Pia Meyer</Link></li>
              <li><Link to="/privatlivspolitik" className="text-stone-200 hover:text-white text-sm transition-colors">Privatlivspolitik</Link></li>
              <li><Link to="/cookiepolitik" className="text-stone-200 hover:text-white text-sm transition-colors">Cookiepolitik</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-700 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-stone-300 text-xs">
            © {new Date().getFullYear()} Meyer Management ApS. Alle rettigheder forbeholdes.
          </p>
          <p className="text-stone-300 text-xs">
            Designet med fokus på ro og tillid.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className={`min-h-screen ${colors.bg} font-sans selection:bg-stone-200`}>
      <ScrollToTop />
      <Header />
      <main className="pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
